import {
  Break,
  dot,
  float,
  If,
  int,
  length,
  Loop,
  max,
  vec2,
} from "three/tsl";

type DepthSample = {
  sample: (uv: unknown) => { r: unknown };
};

type NormalSample = {
  sample: (uv: unknown) => { rgb: { normalize: () => unknown } };
};

type TslF = {
  add: (v: unknown) => TslF;
  mul: (v: unknown) => TslF;
  sub: (v: unknown) => TslF;
};

const asF = (v: unknown) => v as TslF;

/** 0 cross, 1 sobel, 2 prewitt, 3 scharr */
export const GI_EDGE_KERNEL = {
  cross: 0,
  sobel: 1,
  prewitt: 2,
  scharr: 3,
} as const;

const DIRS: [number, number][] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

const oneMinusDot = (a: unknown, b: unknown) =>
  float(1).sub(dot(a as never, b as never) as never);

/**
 * Kenar: MRT normal (1-N·N / sobel N). Sky hâlâ depth. Kernel bir kez, dilate ucuz.
 */
export function depthEdgeMask(args: {
  screenUv: unknown;
  fullResolution: { x: unknown; y: unknown };
  depth: DepthSample;
  normal: NormalSample;
  depthThreshold: unknown;
  thickness: unknown;
  edgeKernel: unknown;
  reversedDepth: unknown;
}): unknown {
  const texelX = float(1).div(float(args.fullResolution.x as never));
  const texelY = float(1).div(float(args.fullResolution.y as never));
  const dThresh = float(args.depthThreshold as never);
  const radius = float(args.thickness as never);
  const kernel = float(args.edgeKernel as never);
  const reversed = float(args.reversedDepth as never);
  const uv0 = args.screenUv;

  const isSkyRaw = (raw: unknown) =>
    (
      reversed as {
        greaterThan: (n: number) => {
          select: (a: unknown, b: unknown) => unknown;
        };
      }
    )
      .greaterThan(0.5)
      .select(
        (raw as { lessThanEqual: (n: number) => unknown }).lessThanEqual(1e-5),
        (raw as { greaterThanEqual: (n: number) => unknown }).greaterThanEqual(
          0.999,
        ),
      );

  const nAt = (uv: unknown) => args.normal.sample(uv).rgb.normalize();

  const cheapRel = (uv: unknown) => {
    const nc = nAt(uv);
    const nl = nAt(
      (uv as { add: (v: unknown) => unknown }).add(vec2(texelX.negate(), 0)),
    );
    const nr = nAt((uv as { add: (v: unknown) => unknown }).add(vec2(texelX, 0)));
    const nd = nAt(
      (uv as { add: (v: unknown) => unknown }).add(vec2(0, texelY.negate())),
    );
    const nu = nAt((uv as { add: (v: unknown) => unknown }).add(vec2(0, texelY)));
    return max(
      oneMinusDot(nc, nl) as never,
      max(
        oneMinusDot(nc, nr) as never,
        max(oneMinusDot(nc, nd) as never, oneMinusDot(nc, nu) as never),
      ) as never,
    );
  };

  const kernelMag = () => {
    const nc = nAt(uv0);
    const nl = nAt(
      (uv0 as { add: (v: unknown) => unknown }).add(vec2(texelX.negate(), 0)),
    );
    const nr = nAt(
      (uv0 as { add: (v: unknown) => unknown }).add(vec2(texelX, 0)),
    );
    const nd = nAt(
      (uv0 as { add: (v: unknown) => unknown }).add(vec2(0, texelY.negate())),
    );
    const nu = nAt(
      (uv0 as { add: (v: unknown) => unknown }).add(vec2(0, texelY)),
    );
    const n00 = nAt(
      (uv0 as { add: (v: unknown) => unknown }).add(
        vec2(texelX.negate(), texelY.negate()),
      ),
    );
    const n02 = nAt(
      (uv0 as { add: (v: unknown) => unknown }).add(vec2(texelX.negate(), texelY)),
    );
    const n20 = nAt(
      (uv0 as { add: (v: unknown) => unknown }).add(vec2(texelX, texelY.negate())),
    );
    const n22 = nAt(
      (uv0 as { add: (v: unknown) => unknown }).add(vec2(texelX, texelY)),
    );
    const crossM = max(
      oneMinusDot(nc, nl) as never,
      max(
        oneMinusDot(nc, nr) as never,
        max(oneMinusDot(nc, nd) as never, oneMinusDot(nc, nu) as never),
      ) as never,
    );
    const sobelGx = asF(n20)
      .add(asF(nr).mul(2))
      .add(n22)
      .sub(asF(n00).add(asF(nl).mul(2)).add(n02));
    const sobelGy = asF(n02)
      .add(asF(nu).mul(2))
      .add(n22)
      .sub(asF(n00).add(asF(nd).mul(2)).add(n20));
    const sobelM = length(sobelGx as never).add(length(sobelGy as never) as never);
    const preGx = asF(n20).add(nr).add(n22).sub(asF(n00).add(nl).add(n02));
    const preGy = asF(n02).add(nu).add(n22).sub(asF(n00).add(nd).add(n20));
    const preM = length(preGx as never).add(length(preGy as never) as never);
    const schGx = asF(n20)
      .mul(3)
      .add(asF(nr).mul(10))
      .add(asF(n22).mul(3))
      .sub(asF(n00).mul(3).add(asF(nl).mul(10)).add(asF(n02).mul(3)));
    const schGy = asF(n02)
      .mul(3)
      .add(asF(nu).mul(10))
      .add(asF(n22).mul(3))
      .sub(asF(n00).mul(3).add(asF(nd).mul(10)).add(asF(n20).mul(3)));
    const schM = length(schGx as never).add(length(schGy as never) as never);
    const k1 = (
      kernel as { lessThan: (n: number) => { select: (a: unknown, b: unknown) => unknown } }
    )
      .lessThan(0.5)
      .select(crossM, asF(sobelM).mul(0.25));
    const k2 = (
      kernel as { lessThan: (n: number) => { select: (a: unknown, b: unknown) => unknown } }
    )
      .lessThan(1.5)
      .select(k1, asF(preM).mul(0.25));
    return (
      kernel as { lessThan: (n: number) => { select: (a: unknown, b: unknown) => unknown } }
    )
      .lessThan(2.5)
      .select(k2, asF(schM).mul(0.1));
  };

  const hit = float(0).toVar();
  const raw0 = args.depth.sample(uv0).r;

  If((radius as { greaterThan: (n: number) => unknown }).greaterThan(0.5) as never, () => {
    If((isSkyRaw(raw0) as { not: () => unknown }).not() as never, () => {
      If(
        (kernelMag() as { greaterThan: (n: unknown) => unknown }).greaterThan(
          dThresh,
        ) as never,
        () => {
          (hit as { assign: (v: unknown) => void }).assign(float(1));
        },
      );
    });
    Loop(
      { start: int(1), end: int(9), type: "int", condition: "<" },
      ({ i }: { i: unknown }) => {
        const k = float(i as never);
        If((k as { lessThanEqual: (n: unknown) => unknown }).lessThanEqual(radius) as never, () => {
          If((hit as { greaterThan: (n: number) => unknown }).greaterThan(0.5) as never, () => {
            Break();
          });
          for (const [dx, dy] of DIRS) {
            const uv = (uv0 as { add: (v: unknown) => unknown }).add(
              vec2(k.mul(texelX).mul(dx), k.mul(texelY).mul(dy)),
            );
            const raw = args.depth.sample(uv).r;
            If((isSkyRaw(raw) as { not: () => unknown }).not() as never, () => {
              If(
                (cheapRel(uv) as { greaterThan: (n: unknown) => unknown }).greaterThan(
                  dThresh,
                ) as never,
                () => {
                  (hit as { assign: (v: unknown) => void }).assign(float(1));
                },
              );
            });
          }
        });
      },
    );
  });

  return (hit as { greaterThan: (n: number) => unknown }).greaterThan(0.5);
}
